import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import usePageMeta from "../../hooks/usePageMeta";
import { api } from "../../lib/api";
import { AirlineCard, RouteCard, SectionHeading } from "../../components/b2c/Blocks";
import { StickySearchCTA, SearchBox } from "../../components/b2c/SearchBox";

const fmt = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
};

const SeasonList = () => {
  const [seasons, setSeasons] = useState([]);
  useEffect(() => {
    api.get("/seasons").then((r) => setSeasons(r.data || [])).catch(() => {});
  }, []);
  if (seasons.length === 0) return null;
  return (
    <div className="mb-14" data-testid="deals-season-list">
      <SectionHeading overline="Seasonal Campaigns" title="Live seasonal deals" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {seasons.map((s) => (
          <Link
            key={s.slug}
            to={`/flights/season/${s.slug}`}
            className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 hover:shadow-xl transition-shadow duration-200"
            data-testid={`season-card-${s.slug}`}
          >
            {s.banner_image_url && (
              <img src={s.banner_image_url} alt={s.title} className="w-full h-44 object-cover opacity-80 group-hover:scale-[1.02] transition-transform duration-500" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5 text-white">
              <p className="font-heading text-lg font-bold leading-tight">{s.title}</p>
              {(s.start_at || s.end_at) && (
                <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-white/85 font-semibold">
                  <Calendar className="w-3 h-3" /> {fmt(s.start_at)} {s.end_at ? `— ${fmt(s.end_at)}` : ""}
                </p>
              )}
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold bg-[#970C10] px-3 py-1.5 rounded-full">
                View deals <ArrowRight className="w-3 h-3" />
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export function Deals() {
  usePageMeta(
    "Flight Deals by Airline — Farevault by Sellvago",
    "Browse discounted fares by airline: Emirates, Qatar Airways, Turkish Airlines, PIA, Saudia, Airblue and Fly Jinnah deal pages."
  );
  const [airlines, setAirlines] = useState([]);
  useEffect(() => {
    api.get("/airlines").then((r) => setAirlines(r.data)).catch(() => {});
  }, []);
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14" data-testid="deals-page">
      <SeasonList />
      <SectionHeading
        overline="Deals by Airline"
        title="Every airline. One discounted search."
        subtitle="Each airline page includes live fare search, baggage guidance, top destinations and booking FAQs."
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="deals-airlines-grid">
        {airlines.map((a) => <AirlineCard key={a.slug} airline={a} />)}
      </div>
      <div className="mt-16">
        <SectionHeading overline="Search now" title="Or search any route directly" />
        <SearchBox page="/flights/deals" />
      </div>
      <StickySearchCTA />
    </div>
  );
}

export function PopularRoutes() {
  usePageMeta(
    "Popular Flight Routes from Pakistan — Farevault by Sellvago",
    "Karachi to Dubai, Lahore to Jeddah, Islamabad to Doha and more — compare live discounted fares on Pakistan's busiest routes."
  );
  const [routes, setRoutes] = useState([]);
  useEffect(() => {
    api.get("/routes").then((r) => setRoutes(r.data)).catch(() => {});
  }, []);
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14" data-testid="popular-routes-page">
      <SectionHeading
        overline="Popular Routes"
        title="Pakistan's busiest corridors, tracked daily"
        subtitle="Route pages include flight times, airline comparisons, fare trends and Umrah-specific guidance."
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="popular-routes-grid">
        {routes.map((r) => <RouteCard key={r.slug} route={r} />)}
      </div>
      <div className="mt-16">
        <SectionHeading overline="Search now" title="Can't see your route? Search it" />
        <SearchBox page="/flights/popular-routes" />
      </div>
      <StickySearchCTA />
    </div>
  );
}
